import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { of } from "rxjs/internal/observable/of";
import { map, switchMap, take, tap } from "rxjs/operators";
import { Currency, currencyList } from "src/app/data/metal-list";
import { BuyRequest } from "src/app/model/wallet/buy-request";
import { ChangeRequest } from "src/app/model/wallet/change-request";
import { SellRequest } from "src/app/model/wallet/sell-request";
import { SendRequest } from "src/app/model/wallet/send-request";
import { WalletResponse } from "src/app/model/wallet/wallet-response";
import { WithdrawRequest } from "src/app/model/wallet/withdraw-request";
import { ServerType } from "src/service/app-init.service";
import { RestApiService } from "src/service/rest-api.service";
import { PriceService } from "../price/price.service";

export interface CurrencyBalance {
  name: Currency;
  gram: number;
  euro: number;
}

@Injectable({
  providedIn: "root",
})
export class WalletService {
  constructor(
    private restApiService: RestApiService,
    private priceService: PriceService
  ) {}

  endpoint = "wallet";

  walletBalance = new BehaviorSubject<CurrencyBalance[]>(null);

  syncWalletBalance() {
    return this.priceService.priceJwt.pipe(
      take(1),
      switchMap((priceJwt) => this.getWallet(priceJwt)),
      tap((walletResponse: WalletResponse) => {
        const currencyList: CurrencyBalance[] = [];
        currencyList.push({
          name: Currency.EURO,
          gram: walletResponse.euro,
          euro: walletResponse.euro,
        });
        currencyList.push({
          name: Currency.GOLD,
          gram: walletResponse.gold,
          euro: walletResponse.goldEuro,
        });
        currencyList.push({
          name: Currency.SILVER,
          gram: walletResponse.silver,
          euro: walletResponse.silverEuro,
        });
        currencyList.push({
          name: Currency.PLATIN,
          gram: walletResponse.platinium,
          euro: walletResponse.platiniumEuro,
        });
        currencyList.push({
          name: Currency.PALLADIUM,
          gram: walletResponse.palladium,
          euro: walletResponse.palladiumEuro,
        });

        this.walletBalance.next(currencyList);
      })
    );
  }

  getDebts() {
    return this.restApiService.get(ServerType.WALLET, this.endpoint + "/debts")
  }

  payDebt(debtId:String){
    return this.restApiService.post(ServerType.WALLET, this.endpoint+"/pay-debts/"+debtId, null)
  }

  getCurrencyEuroAmount(currency: Currency) {
    return this.walletBalance.value.find((item) => item.name == currency)?.euro;
  }

  getAmountByEuro(currency: Currency, euro: number, buy: boolean) {
    return this.getPriceByCurrency(currency, buy).pipe(
      switchMap((price) => of(euro / price))
    );
  }

  getEuroByAmount(currency: Currency, weight: number, buy: boolean) {
    const priceObject = this.priceService.priceList.value.find(
      (item) => item.name == currency
    );

    const price = buy ? priceObject.buy : priceObject.sell;

    return of(price * weight);
  }

  getPriceByCurrency(currency: Currency, buy: boolean) {
    const priceObject = this.priceService.priceList.value.find(
      (item) => item.name == currency
    );

    const price = buy ? priceObject.buy : priceObject.sell;
    return of(price);
  }

  getChangeAmount(
    sourceGram: number,
    targetGram: number,
    sourceCurrency: Currency,
    targetCurrency: Currency
  ) {
    const sourcePrice = this.priceService.priceList.value.find(
      (item) => item.name == sourceCurrency
    ).sell;

    const targetPrice = this.priceService.priceList.value.find(
      (item) => item.name == targetCurrency
    ).buy;

    if (sourceGram) {
      const sourceTotalPrice = sourceGram * sourcePrice;
      return of(sourceTotalPrice / targetPrice);
    } else {
      const targetTotalPrice = targetPrice * targetGram;
      return of(targetTotalPrice / sourcePrice);
    }
  }

  getWallet(priceJwt: String) {
    return this.restApiService.post(ServerType.WALLET, this.endpoint + "/get", {
      priceJwt,
    });
  }

  getMyCurrencyList() {
    return this.walletBalance.pipe(
      map((currencyList) =>
        currencyList.filter(
          (item) => item.name !== Currency.EURO && item.gram != 0
        )
      )
    );
  }

  getLabel(currencyItem) {
    return of(
      currencyList.find((item) => item.name == currencyItem.name).label
    );
  }

  buy(buyRequest: BuyRequest) {
    return this.restApiService
      .post(ServerType.WALLET, this.endpoint + "/buy", buyRequest)
      .pipe(switchMap(() => this.syncWalletBalance()));
  }

  sell(sellRequest: SellRequest) {
    return this.restApiService
      .post(ServerType.WALLET, this.endpoint + "/sell", sellRequest)
      .pipe(switchMap(() => this.syncWalletBalance()));
  }

  change(changeRequest: ChangeRequest) {
    return this.restApiService
      .post(ServerType.WALLET, this.endpoint + "/change", changeRequest)
      .pipe(switchMap(() => this.syncWalletBalance()));
  }

  send(sendRequest: SendRequest) {
    return this.restApiService
      .post(ServerType.WALLET, this.endpoint + "/send", sendRequest)
      .pipe(switchMap(() => this.syncWalletBalance()));
  }

  withdraw(withdrawRequest: WithdrawRequest) {
    return this.restApiService
      .post(ServerType.WALLET, this.endpoint + "/withdraw", {
        ...withdrawRequest,
        priceJwt: this.priceService.priceJwt.value,
      })
      .pipe(switchMap(() => this.syncWalletBalance()));
  }
}
